import { useRef, useState } from "react";
import { Autocomplete } from "@react-google-maps/api";
import {
  Controller,
  useFormContext,
  ControllerRenderProps,
  FieldPath,
} from "react-hook-form";
import FormTextInput from "./form-text-input";
import { MapPinDrop } from "./map-pin-drop";
import { Button } from "@/shared/components/ui/button";

type LocationDetails = {
  city: string;
  state: string;
  country: string;
  latitude?: number;
  longitude?: number;
  zipCode?: string;
};

interface StateCountryAutocompleteProps<T> {
  setLocationDetails: (location: LocationDetails) => void;
  locationDetails: LocationDetails | null;
  control: any; // ideally replace with `Control<T>`
  fieldPrefix?: string; // Optional prefix for nested forms (e.g., "form1" for "form1.home.address")
  onSelectionChange?: (selected: boolean) => void; // Callback when selection state changes
}

// 👇 Utility
function toLatLngLiteral(
  loc: google.maps.LatLng | google.maps.LatLngLiteral
): google.maps.LatLngLiteral {
  if (typeof (loc as google.maps.LatLng).lat === "function") {
    const ll = loc as google.maps.LatLng;
    return { lat: ll.lat(), lng: ll.lng() };
  }
  return loc as google.maps.LatLngLiteral;
}

export const RegisterStateCountryAutocomplete = <
  T extends Record<string, any>
>({
  setLocationDetails,
  locationDetails,
  control,
  fieldPrefix = "", // Default to empty string for root level
  onSelectionChange,
}: StateCountryAutocompleteProps<T>) => {
  const { setValue, clearErrors } = useFormContext<T>();
  const [error, setError] = useState<string>("");
  const [selected, setSelected] = useState(false);
  const [showMapPinDrop, setShowMapPinDrop] = useState(false);
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);
  
  // Build field path with optional prefix
  const getFieldPath = (field: string): FieldPath<T> => {
    const path = fieldPrefix ? `${fieldPrefix}.home.${field}` : `home.${field}`;
    return path as FieldPath<T>;
  };

  const openMapPinDrop = () => {
    clearErrors(getFieldPath("address") as any);
    setError("");
    setShowMapPinDrop(true);
  };

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    acRef.current = autocomplete;
  };

  const handlePlaceChanged = (onChange: (val: string) => void) => {
    if (!acRef.current) return;

    const place = acRef.current.getPlace();
    if (!place || !place.geometry?.location || !place.formatted_address) return;

    const components = place.address_components;
    if (!components) return;

    let city = "";
    let stateShortName = "";
    let countryShortName = "";
    let zipCode = "";

    components.forEach((component) => {
      const types = component.types;
      if (types.includes("locality")) city = component.long_name;
      if (types.includes("administrative_area_level_1"))
        stateShortName = component.long_name;
      if (types.includes("country")) countryShortName = component.long_name;
      if (types.includes("postal_code")) zipCode = component.long_name;
    });

    if (!city || !stateShortName || !countryShortName) {
      setError(
        "Please select a valid address that includes city, state, and country."
      );
      setSelected(false);
      if (onSelectionChange) onSelectionChange(false);
      return;
    }

    const { lat, lng } = toLatLngLiteral(place.geometry.location);

    setSelected(true);
    setError("");
    if (onSelectionChange) onSelectionChange(true);

    const newLocation: LocationDetails = {
      city,
      state: stateShortName,
      country: countryShortName,
      latitude: lat,
      longitude: lng,
      zipCode,
    };

    setLocationDetails(newLocation);

    // Use field prefix if provided (e.g., "form1.home.address" instead of "home.address")
    setValue(getFieldPath("address"), place.formatted_address as any);
    setValue(getFieldPath("city"), city as any);
    setValue(getFieldPath("state"), stateShortName as any);
    setValue(getFieldPath("country"), countryShortName as any);
    setValue(getFieldPath("zipCode"), zipCode as any);
    setValue(getFieldPath("latitude"), lat as any);
    setValue(getFieldPath("longitude"), lng as any);
  };

  const handleMapLocationSelected = (location: LocationDetails) => {
    // Fires only after "Use This Location"; MapPinDrop has set form fields including home.address.
    setLocationDetails(location);
    setSelected(true);
    setShowMapPinDrop(false);
    if (onSelectionChange) onSelectionChange(true);
    setError("");
  };

  if (showMapPinDrop) {
    return (
      <div className="w-full">
        <MapPinDrop<T>
          onLocationSelected={handleMapLocationSelected}
          onClose={() => setShowMapPinDrop(false)}
          control={control}
          fieldPrefix={fieldPrefix}
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <Controller<T, FieldPath<T>>
        name={getFieldPath("address")}
        control={control}
        render={({ field }: { field: ControllerRenderProps<T, FieldPath<T>> }) => (
          <Autocomplete
            onLoad={handleLoad}
            onPlaceChanged={() => handlePlaceChanged(field.onChange)}
            options={{ types: ["address"] }}
          >
            <FormTextInput
              inputGroupProps={{
                label: "Street Address",
              }}
              placeholder="Address (including city, state and zip)"
              name={fieldPrefix ? `${fieldPrefix}.home.address` : "home.address"}
              control={control}
              value={field.value || ""}
              onChange={(e) => {
                field.onChange(e.target.value);
                setSelected(false);
                if (onSelectionChange) onSelectionChange(false);
              }}
            />
          </Autocomplete>
        )}
      />

      {/* "I don't have a street address" link */}
      <div className="mt-2">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm text-blue-600 hover:text-blue-700 hover:underline"
          onClick={openMapPinDrop}
          showLoader={false}
        >
          I don&apos;t have a street address
        </Button>
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};
